// ============================================================
//  VISHAL BEHERA — PORTFOLIO CONTENT
//  ------------------------------------------------------------
//  THIS IS THE ONLY FILE YOU NEED TO EDIT.
//  Everything marked [PLACEHOLDER] is realistic sample content —
//  replace it with your real details. All fields are optional;
//  delete a field (or set it to null) and the UI adapts.
// ============================================================

export const profile = {
  name: "Vishal Behera",
  initials: "VB",
  tagline: "Full-Stack & App Developer • Builder • Entrepreneur",
  roles: [
    "Full-Stack Developer",
    "App Developer",
    "Software Engineer",
    "Backend Developer",
    "Freelancer",
    "Entrepreneur",
  ],
  email: "beheravishal19@gmail.com",
  phone: "+91 90784 34144",
  location: "Sundargarh, Odisha — working worldwide",
  availability: "Available for selected freelance & product opportunities",
  socials: {
    github: "https://github.com/Bee0510",
    linkedin: "https://www.linkedin.com/in/vlshalbehera",
    twitter: null, // add your X/Twitter URL here if you have one
  },
  resumeUrl: "/resumes/resume-app-developer.pdf",
  resumeVariants: [
    { label: "App Developer", url: "/resumes/resume-app-developer.pdf" },
    { label: "Backend Developer", url: "/resumes/resume-backend-developer.pdf" },
    { label: "React Native", url: "/resumes/resume-react-native-developer.pdf" },
  ],
};

export const hero = {
  headline: ["Building Digital Products", "That Turn Ideas", "Into Reality."],
  sub: "Full-Stack & App Developer — building scalable web platforms, mobile apps and backend systems, whether embedded with an engineering team or shipping end-to-end for startups and businesses.",
  openToRoles: "Open to full-time software engineering roles",
  metrics: [
    { value: "1+", label: "Years Experience" },
    { value: "6+", label: "Projects Shipped" },
    { value: "5K+", label: "Connected Devices" },
  ],
};

// Two ways to work with me — recruiters and founders get equal footing.
export const paths = {
  recruiter: {
    eyebrow: "For Recruiters & Engineering Teams",
    title: "Hire an engineer who ships.",
    points: [
      "Production experience across mobile apps, backends and IoT systems",
      "Full-stack ownership — Flutter, React Native, Node.js, PostgreSQL, AWS/GCP",
      "Real-time MQTT calibration system with 84% success across 5K+ devices",
      "Shipped 5+ production features at Zimplistic for 5K+ active users",
    ],
    primary: { label: "Download Resume", kind: "resume" },
    secondary: { label: "See My Experience", target: "experience" },
  },
  founder: {
    eyebrow: "For Founders & Businesses",
    title: "Build your product with me.",
    points: [
      "MVPs taken from idea to production, fast and pragmatically",
      "Mobile apps, web platforms, APIs and backend systems end to end",
      "Business-first thinking — scope, budget and ROI, not just code",
      "Long-term partner for maintenance and iteration after launch",
    ],
    primary: { label: "Start a Conversation", target: "contact" },
    secondary: { label: "View My Work", target: "work" },
  },
};

export const intro = {
  heading: "More Than Just a Developer.",
  body: [
    "I work at the intersection of engineering and business — designing, building and shipping products that solve real problems. From full-stack web platforms to mobile apps and backend systems serving thousands of connected devices, I own the journey from concept to deployment.",
    "Beyond client work, I build my own products and explore venture ideas. I understand deadlines, budgets and ROI as well as I understand databases, APIs and deployment pipelines.",
  ],
  stats: [
    { value: "1+", label: "Years Professional Experience", note: "Production systems, real users" },
    { value: "6+", label: "Projects Delivered", note: "Web, mobile & backend" },
    { value: "5,000+", label: "Connected Devices", note: "IoT systems in production" },
    { value: "3×", label: "Stack Coverage", note: "Web + Mobile + Backend" },
  ],
};

export const services = [
  {
    icon: "Layers",
    title: "Full-Stack Development",
    description: "Scalable web applications, APIs and backend systems — designed, built and shipped end to end.",
    deliverables: ["Web applications", "REST APIs", "System architecture"],
  },
  {
    icon: "Smartphone",
    title: "Mobile App Development",
    description: "Production-ready mobile applications with modern cross-platform technologies.",
    deliverables: ["React Native", "Flutter", "Store deployment"],
  },
  {
    icon: "Server",
    title: "Backend & API Development",
    description: "REST APIs, databases, authentication systems and backend architectures built to scale.",
    deliverables: ["API design", "Auth & security", "Database modeling"],
  },
  {
    icon: "Rocket",
    title: "MVP & Product Development",
    description: "Turn an idea into a functional MVP — then take it toward production with the right foundations.",
    deliverables: ["Rapid prototyping", "Product strategy", "Launch support"],
  },
  {
    icon: "Briefcase",
    title: "Freelance Development",
    description: "Helping businesses and startups build, improve and maintain their digital products.",
    deliverables: ["Feature development", "Maintenance", "Code rescue"],
  },
  {
    icon: "Compass",
    title: "Technical Consulting",
    description: "Choosing the right technologies, architecture and implementation strategy for your business.",
    deliverables: ["Tech selection", "Architecture review", "Roadmapping"],
  },
];

// ------------------------------------------------------------
//  PROJECTS — add a new object here and it appears automatically.
//  category: "client" | "professional" | "freelance" | "personal" | "product"
// ------------------------------------------------------------
export const projects = [
  {
    title: "Peter Fresh",
    slug: "peter-fresh",
    category: "client",
    type: "Retail Platform · Mobile App + Admin Dashboard",
    description: "Production app and admin dashboard for a farm-to-consumer store — customers browse live products, prices and stock before visiting.",
    longDescription: "Full-stack retail platform: Flutter customer app, Node.js/Express + PostgreSQL backend, and a centralized admin dashboard for catalog, vendors, banners and role-based access.",
    role: "Full-Stack Developer",
    technologies: ["Flutter", "Node.js", "Express.js", "PostgreSQL"],
    image: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1200&q=80",
    gallery: [],
    github: null,
    liveUrl: null,
    playStore: null, // paste your Play Store URL here and the button appears
    client: "Retail client",
    year: "2025",
    status: "Live",
    achievements: ["Live on the Play Store", "Catalog, inventory & banners managed from one dashboard", "Secure role-based authentication"],
    problem: "A farm-to-consumer store had no digital shelf — customers couldn't see available products, prices or stock before visiting, and staff ran operations manually.",
    solution: "Built a Flutter customer app backed by Node.js/Express and PostgreSQL, with an admin dashboard covering catalog, category and vendor management plus role-based access.",
    challenges: "Designing image upload workflows and fast search/filtering over a growing catalog while keeping the Flutter UI responsive.",
    outcome: "Live on the Play Store; the store's entire product operation now runs through one centralized dashboard.",
  },
  {
    title: "Apniseva Technician",
    slug: "apniseva-technician",
    category: "freelance",
    type: "On-Demand Services · Mobile App",
    description: "Cross-platform app connecting users with on-demand service providers — bookings, payments and service inquiries in one flow.",
    longDescription: "Launched a Flutter marketplace app for local services with booking workflows, Razorpay payments and Provider state management over REST APIs.",
    role: "App Developer",
    technologies: ["Flutter", "REST APIs", "Provider", "Razorpay"],
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=80",
    gallery: [],
    github: null,
    liveUrl: null,
    playStore: null, // paste your Play Store URL here and the button appears
    client: "Apniseva",
    year: "2024",
    status: "Live",
    achievements: ["100+ downloads on Play Store", "200+ bookings facilitated", "+15% service inquiries", "+25% service delivery efficiency"],
    problem: "Local service providers had no structured way to receive bookings or payments — everything ran on phone calls and chat messages.",
    solution: "Designed and launched a cross-platform Flutter app with intuitive booking flows, Razorpay payment integration and REST API backends.",
    challenges: "Keeping booking and payment flows reliable for non-technical users on low-end devices and patchy networks.",
    outcome: "100+ downloads and 200+ bookings shortly after launch, with service inquiries up 15% and delivery efficiency up 25%.",
  },
  {
    title: "Grosoon",
    slug: "grosoon",
    category: "product",
    type: "Grocery Marketplace · Backend + Mobile",
    description: "Marketplace connecting customers, shops and delivery partners — with an engine that splits one order across multiple shops by price and PIN code.",
    longDescription: "My own product: complete backend and mobile app for a grocery marketplace with OTP auth, role-based dashboards and a smart allocation engine that maximizes margin on every order.",
    role: "Founder & Engineer",
    technologies: ["Node.js", "Supabase", "PostgreSQL", "Razorpay", "Render"],
    image: "https://images.unsplash.com/photo-1518843875459-f738682238a6?auto=format&fit=crop&w=1200&q=80",
    gallery: [],
    github: null,
    liveUrl: null,
    client: null,
    year: "2026",
    status: "Building",
    achievements: ["Smart multi-shop order allocation engine", "OTP auth + role-based dashboards for 4 user types", "Razorpay payments integrated"],
    problem: "Local grocery delivery needs one customer order fulfilled by multiple shops — each with different prices and stock — without destroying margin.",
    solution: "Designed a Node.js + PostgreSQL/Supabase backend whose allocation engine distributes items across eligible shops by shopkeeper pricing and customer PIN code, auto-generating shop-specific sub-orders.",
    challenges: "Maximizing margin while splitting a single order across shops with conflicting prices and inventory.",
    outcome: "End-to-end marketplace workflow running with payments; currently in active development toward launch.",
  },
  {
    title: "Redrabbit Wellness",
    slug: "redrabbit-wellness",
    category: "freelance",
    type: "HealthTech · Marketplace Backend",
    description: "Diagnostics marketplace backend — compare labs, book slots, manage samples and reports, with EMI plans and automated WhatsApp reminders.",
    longDescription: "Node.js backend for a diagnostics marketplace with multi-lab order splitting, product snapshots for historical pricing, automated slot generation, Razorpay + EMI workflows and Supabase cron notifications.",
    role: "Backend Developer",
    technologies: ["Node.js", "Next.js", "Supabase", "Razorpay", "Render"],
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=80",
    gallery: [],
    github: null,
    liveUrl: null,
    client: "Redrabbit Wellness",
    year: "2025",
    status: "Live",
    achievements: ["Multi-lab orders in a single cart", "EMI workflows with admin-configurable limits", "Automated WhatsApp payment reminders via cron"],
    problem: "Patients couldn't compare tests across labs or book diagnostics with flexible payments; every lab managed orders manually.",
    solution: "Built a backend that splits one order across multiple labs, snapshots product data to preserve historical pricing, and automates slots, payments, EMI and reminders.",
    challenges: "Preserving price and test integrity at purchase time — even after labs change their catalogs — while splitting orders by lab.",
    outcome: "Live platform processing multi-lab diagnostic orders with EMI plans and automated payment follow-ups.",
  },
  {
    title: "Recipease",
    slug: "recipease",
    category: "personal",
    type: "AI Mobile App · Full-Stack Solo Build",
    description: "AI-powered recipe discovery — React Native app, Node.js backend and a MongoDB caching layer that answers before ever calling the LLM.",
    longDescription: "Independently designed and deployed a full-stack recipe app: OpenAI API generates structured recipes (ingredients, steps, time, calories, nutrition), persisted in MongoDB and served cache-first on AWS.",
    role: "Design & Development — Solo",
    technologies: ["React Native", "Node.js", "Express.js", "MongoDB", "OpenAI API", "AWS"],
    image: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=1200&q=80",
    gallery: [],
    github: "https://github.com/Bee0510",
    liveUrl: null,
    client: null,
    year: "2025",
    status: "Live",
    achievements: ["97% search accuracy", "Database-first LLM caching cuts latency & API cost", "Solo build: mobile, backend, cloud deploy"],
    problem: "Generic recipe search returns irrelevant results, and generating structured recipes with an LLM on every request is slow and expensive.",
    solution: "Built a React Native + Express app where a MongoDB cache serves known recipes first, falling back to the OpenAI API only for genuinely new searches.",
    challenges: "Structuring free-form LLM output into a reliable recipe schema — ingredients, steps, cooking time, calories and nutrition — without manual cleanup.",
    outcome: "97% search accuracy with dramatically lower latency and API spend thanks to the cache-first architecture.",
  },
];

export const projectCategories = [
  { id: "all", label: "All" },
  { id: "client", label: "Client Work" },
  { id: "professional", label: "Professional" },
  { id: "freelance", label: "Freelance" },
  { id: "personal", label: "Personal" },
  { id: "product", label: "Product" },
];

// ------------------------------------------------------------
//  EXPERIENCE — [PLACEHOLDER] company names. Replace with real ones.
// ------------------------------------------------------------
export const experience = [
  {
    role: "Software Engineer — Mobile & Backend",
    company: "Zimplistic India Pvt. Ltd.",
    duration: "Sep 2025 — Present",
    description: "Building the connected-device experience for Rotimatic — mobile workflows, firmware communication and backend services for 5,000+ devices in production.",
    responsibilities: [
      "Built the mobile workflow for an intelligent roti quality-correction system — processing computer-vision outputs and sending real-time MQTT calibration commands to firmware",
      "Engineered a fault-tolerant MQTT framework with intelligent reconnection, offline recovery and state synchronization across 5K+ devices",
      "Shipped 5+ production features: warranty management, machine diagnostics and device lifecycle workflows",
      "Developed the Node.js backend on GCP integrating HubSpot chat into the app, with AWS Cognito authentication and API-key secured endpoints",
    ],
    technologies: ["Flutter", "Node.js", "MQTT", "AWS Cognito", "GCP", "Firebase"],
    achievements: ["84% correction success rate across 5K+ connected devices", "Production features used daily by 5K+ active users"],
  },
  {
    role: "App Developer — Internship",
    company: "Jazzee Technologies",
    duration: "Nov 2024 — Aug 2025",
    description: "Built the Jazzee EdTech mobile app for iOS and Android — real-time classroom attendance with identity validation.",
    responsibilities: [
      "Enabled students to mark real-time classroom attendance with secure face validation using QR and non-QR methods",
      "Integrated attendance tracking with detailed reports, improving transparency and accountability",
    ],
    technologies: ["Flutter", "REST APIs", "Firebase"],
    achievements: ["Shipped to both iOS and Android", "Secure face-validated attendance flow"],
  },
  {
    role: "App Development Intern",
    company: "Syflex Techno Solution",
    duration: "Apr 2024 — Nov 2024",
    description: "Crafted a high-performance eCommerce app in Flutter with extensive API integration.",
    responsibilities: [
      "Built the eCommerce app with seamless state management and extensive API integration",
      "Implemented secure payment systems for a reliable checkout experience",
      "Optimized product management features and app performance",
    ],
    technologies: ["Flutter", "REST APIs", "Payment Gateways"],
    achievements: ["+12% app efficiency", "10% faster product load times"],
  },
];

// ------------------------------------------------------------
//  VENTURES — things you're building beyond client work.
// ------------------------------------------------------------
export const ventures = [
  {
    name: "Grosoon",
    logo: null, // drop an image URL here later
    description: "My own grocery marketplace — a smart allocation engine that splits customer orders across local shops by price and PIN code, with apps for customers, shopkeepers, delivery partners and admins.",
    role: "Founder & Engineer",
    status: "Building", // Building | Live | Experiment | Coming Soon
    link: null,
    technologies: ["Node.js", "Supabase", "PostgreSQL", "Razorpay"],
  },
  {
    name: "Recipease",
    logo: null,
    description: "AI-powered recipe discovery product — LLM-generated structured recipes served cache-first, designed, built and deployed solo.",
    role: "Founder & Engineer",
    status: "Live",
    link: null,
    technologies: ["React Native", "Node.js", "MongoDB", "AWS"],
  },
  {
    name: "Independent Engineering Practice",
    logo: null,
    description: "Client work under my own name — production apps and backends for marketplace, retail, services and healthtech businesses.",
    role: "Freelance Engineer",
    status: "Live",
    link: null,
    technologies: [],
  },
  {
    name: "Next Product Concept", // [PLACEHOLDER] — rename when your next venture is real
    logo: null,
    description: "A new product idea currently in validation — talking to potential users before writing production code.",
    role: "Founder",
    status: "Coming Soon",
    link: null,
    technologies: [],
  },
];

export const techStack = [
  {
    group: "Languages",
    items: ["JavaScript", "Java", "Dart", "C", "Kotlin"],
  },
  {
    group: "Frontend / Mobile",
    items: ["React", "React Native", "Flutter"],
  },
  {
    group: "Backend",
    items: ["Node.js", "Express.js", "Spring Boot", "MQTT"],
  },
  {
    group: "Databases",
    items: ["PostgreSQL", "MongoDB", "Supabase", "Firebase"],
  },
  {
    group: "Cloud / Infrastructure",
    items: ["AWS", "Google Cloud", "Vercel", "Render"],
  },
  {
    group: "Tools",
    items: ["Git", "GitHub", "VS Code", "Android Studio", "Xcode", "Expo"],
  },
];

export const caseStudies = [
  {
    title: "Real-time calibration across 5,000+ connected devices",
    context: "Zimplistic — Rotimatic Quality Correction",
    steps: [
      { label: "Problem", text: "Roti quality drifted across devices in the field, with no closed loop between what the camera saw and what the machine did next." },
      { label: "Requirement", text: "Correct quality in real time across a fleet of 5,000+ connected devices, over unreliable home networks." },
      { label: "Approach", text: "Built a mobile workflow that turns computer-vision output into calibration commands, delivered to firmware over MQTT." },
      { label: "Tech Choices", text: "Flutter for the app, MQTT for real-time device messaging, Node.js on GCP for services, AWS Cognito for identity." },
      { label: "Implementation", text: "Engineered a fault-tolerant MQTT framework — intelligent reconnection, offline recovery and state synchronization for varying network conditions." },
      { label: "Challenges", text: "Keeping firmware communication reliable when home Wi-Fi drops mid-command, without corrupting device state." },
      { label: "Result", text: "84% correction success rate across the 5K+ device fleet, shipped alongside diagnostics and warranty features." },
    ],
  },
  {
    title: "Splitting one grocery order across many shops",
    context: "Grosoon — Smart Order Allocation",
    steps: [
      { label: "Problem", text: "A customer's grocery order rarely fits one shop — items live in different stores at different prices." },
      { label: "Requirement", text: "Fulfill each order from the best combination of shops while protecting margin and delivery promise." },
      { label: "Approach", text: "Designed an allocation engine that scores eligible shops by PIN code and shopkeeper pricing, then auto-generates shop-specific sub-orders." },
      { label: "Tech Choices", text: "Node.js backend, PostgreSQL via Supabase for relational order data, Razorpay for payments, Render for deployment." },
      { label: "Implementation", text: "OTP auth, role-based dashboards for customers, shopkeepers, delivery partners and admins, plus admin-controlled price filtering." },
      { label: "Challenges", text: "Maximizing client margin while honoring per-shop prices and stock — allocation is a constrained optimization on every order." },
      { label: "Result", text: "End-to-end marketplace workflow running with payments; listings exceeding configured selling price are filtered automatically." },
    ],
  },
];

export const freelance = {
  heading: "Have a Product Idea?",
  body: "Whether you're starting from an idea, improving an existing product, or looking for someone to take your application from development to production, I can help.",
  capabilities: [
    "MVP development",
    "Mobile apps",
    "Web applications",
    "Backend systems",
    "API development",
    "Database design",
    "Cloud deployment",
    "Existing application improvements",
  ],
};

// [PLACEHOLDER] — Replace with real client logos / partnerships.
// Testimonials are intentionally empty: add real quotes only.
export const collaborations = {
  partners: [
    { name: "[Client Logo]" },
    { name: "[Company Logo]" },
    { name: "[Partner Logo]" },
    { name: "[Brand Logo]" },
  ],
  testimonials: [],
};

export const about = {
  heading: "Behind the Code",
  paragraphs: [
    "I build because shipping something real beats talking about ideas. Every project — client work, freelance gig or my own venture — is an exercise in turning ambiguity into a working product someone can use.",
    "My philosophy is simple: understand the business problem first, choose boring-reliable technology second, and polish the experience until it feels effortless. The best engineering is invisible — it just works.",
    "Long term, I'm building toward running my own product company. Every system I ship and every client I work with is a step on that path.",
  ],
  principles: [
    { title: "Outcomes over output", text: "Code is a means. Shipped, useful product is the goal." },
    { title: "Own it end to end", text: "From database schema to the last pixel — full responsibility." },
    { title: "Learn in public", text: "Every project teaches something worth reusing on the next one." },
    { title: "Build toward independence", text: "Client work funds the ventures; the ventures fund the vision." },
  ],
  interests: ["Product design", "Startups", "IoT & hardware", "Open source", "System design"],
  education: {
    degree: "B.Tech, Mechanical Engineering",
    school: "National Institute of Technology Rourkela",
    years: "2021 — 2025",
  },
  // Your photo, served from public/portrait.jpg — replace the file to change it.
  portrait: "/portrait.jpg",
};

export const marqueeItems = [
  "Full-Stack Development",
  "Mobile Apps",
  "Backend Systems",
  "MVP Development",
  "API Design",
  "Cloud Architecture",
  "Product Strategy",
  "From Concept to Deployment",
];
